import React from 'react';
import { css } from 'emotion';
import { Table } from 'antd';
import SimpleMessage from 'components/core/help/SimpleMessage';
import NavigationUtils from 'utils/NavigationUtils';
import Constants from 'utils/Constants';

import rangeInputImg from 'images/info/range_input.png';
import rangeOutputImg from 'images/info/range_output.png';
import rangeAlignmentImg from 'images/info/range_alignment.png';

import qcMaskInputImg from 'images/info/qc_mask_input.png';
import qcMaskOutputImg from 'images/info/qc_mask_output.png';

import qcBadInputImg from 'images/info/qc_bad_input.png';
import qcBadOutputImg from 'images/info/qc_bad_output.png';

import alignmentOutputImg from 'images/info/alignment_output.png';
import alignmentInputDataImg from 'images/info/alignment_input_data.png';
import alignmentOutputDataImg from 'images/info/alignment_output_data.png';
import alignmentShiftImg from 'images/info/alignment_shift.png';

import standardDeviationInputImg from 'images/info/std_deviation_input.png';
import standardDeviationOutputImg from 'images/info/std_deviation_output.png';

import goodfracInputImg from 'images/info/goodfrac_input.png';
import goodfracOutputImg from 'images/info/goodfrac_output.png';
import goodfracDiagramImg from 'images/info/goodfrac_diagram.png';

const dataLocationDiv = css`
    margin-bottom: 20px;
`;

const transformParameterMessageStyles = css`
    width:725px;
    height:400px;
    overflow: auto;
    img {
        max-width:700px;
    }
`;

const templateTableStyle = css`
    max-width: 800px;
    .ant-table table {
        background-color: white;
    }
    .ant-table-thead > tr > th {
        font-weight: bold;
    }
    tr {
        vertical-align: top;
    }
    
`;

const dodUrl = process.env.REACT_APP_DOD_URL; // injected by Node.js build environment parameters

/**
 * This class defines all help messages for the application.  I had to move it from Constants into
 * a react component class because react will not let you reference JSX from outside a react context.
 */
export default class InfoMessages extends React.Component {

    static processName = (
        <SimpleMessage>
            The name of the process.  If using an ADI code project, the executable will be the name followed by _vap or _ingest.
            <p>Process name may only contain letters, numbers, or underscore characters.</p>
        </SimpleMessage>
    );

    static description = (
        <SimpleMessage>
            Please provide an optional description of what this process does if it's not clear from the name.
        </SimpleMessage>
    );

    static deprecated = (
        <SimpleMessage>
            This PCM Process is no longer used to process or reprocess data.
        </SimpleMessage>
    );

    static codeUrl = (
        <SimpleMessage>
            If this process uses an ADI code project, provide the URL where the code is stored.
            (It is recommended that all code is stored at https://code.arm.gov.)
        </SimpleMessage>
    );

    static runLocations = (
        <SimpleMessage>
            List all the ARM site/facility pairings for which the process is valid to run.
            Can only run at facilities that are documented in the DSDB.
        </SimpleMessage>
    );

    static runLocation = (
        <SimpleMessage>
            List the ARM site/facility pairing for which the process is valid to run.
            Can only run at facilities that are documented in the DSDB.
        </SimpleMessage>
    );

    static processingInterval = (
        <SimpleMessage>
            The data batch size (in seconds) that is processed by ADI in a single chunk (and written to a single
            output file).  If set to 0, the batch size will be equal to the begin and end dates specified when running
            the process, plus any time offsets.
        </SimpleMessage>
    );

    static processType = (
        <SimpleMessage>
            <p>Defines the type of data produced by the process.</p>
            <p>Select <b>VAP (Value Added Product)</b> If the process produces higher order datastreams from ARM standard datastreams.</p>
            <p>Select <b>Ingest</b> if the process converts raw data into ARM standard format. </p>
        </SimpleMessage>
    );

    static processSubtype = (
        <SimpleMessage>
            <p>Defines the data processing mechanism used by the process.</p>
            <p>Select <b>ADI</b> If the process will be retrieving data from netCDF data files.</p>
            <p>Select <b>Basic</b> if the process will be reading non-netCDF input files. </p>
        </SimpleMessage>
    );

    static importVariables = (
        <SimpleMessage>
            <p style={{ marginBottom: '20px' }}>Select the datastream and variables that you would like to use as input to this process.</p>
            <p>You can type in name for which no DOD exists, however a DOD definition is recommended to correctly set transformation parameters.  For optimal
                PCM usability, you should create a DOD by opening the
                <a style={{ fontWeight: 'bold', padding: '0 5px' }} target="_blank" rel="noopener noreferrer" onClick={NavigationUtils.openExternalLinkInNewWindowFunc(dodUrl)}>
                    DOD Manager
                </a>
                and importing a NetCDF file for this datastream.
            </p>
        </SimpleMessage>
    );

    static selectInputDataset = (
        <SimpleMessage>
            <p style={{ marginBottom: '20px' }}>Select the retrieval rule set that you would like to use for importing these variables.</p>
            <p>If <strong>AUTO-SELECT</strong> is chosen, then the input dataset will be automatically determined.
                If there is already a retrieval rule set defined for the selected datstream (where the selected
                datastream is the primary datastream in the rule set), then that rule set will be chosen.  If there
                are multiple rule sets defined for the same primary dataset, then the first one will be chosen.  If
                there are no rule sets defined for the given dataset, then a new one will be created and given the name
                of the datastream.
            </p>
            <p><strong>If you would like to force the creation of a new retrieval rule set, enter a new name.</strong></p>
        </SimpleMessage>
    );

    static retrievedVariableUnits = (
        <SimpleMessage>
            The process will automatically convert input variable data into these units.
            <p><b>SI Units</b> are required.</p>
        </SimpleMessage>
    );

    static retrievedVariableName = (
        <SimpleMessage>
            This is the variable name that will be used in the process.  All code will refer to variables by this name.
        </SimpleMessage>
    );

    static inputDatastreamVariableName = (
        <SimpleMessage>
            This is the original variable name from the input datastream.
        </SimpleMessage>
    );

    static dataLocations = (
        <SimpleMessage>
            <div className={dataLocationDiv}>
                Select the server directories where the process should look for input datastream files.
            </div>
            <div className={dataLocationDiv}>
                Note that if <b>User folder</b> is selected, you should point to a typical data/datastream folder.
                The folder name should <b>NOT</b> contain the site, facility, or datastream name, as this subpath
                will be automatically determined by the data consolidator.
            </div>
            <div className={dataLocationDiv}>
                For example, if you wanted to use data files from DMF folder <b>/data/home/gaustad/pccp/DATA/data/datastream/sgp/sgppccpbE45.c1</b>,
                then you should enter <b>/data/home/gaustad/pccp/DATA/data/datastream</b> for the user folder.
            </div>
        </SimpleMessage>
    );

    static debugLevel = (
        <SimpleMessage>
            <div className={dataLocationDiv}>
                Turning on debug logging provides more information on the console about each stage of the ADI process
                which can be useful when debugging errors.  The following levels can be set via the user interface:
            </div>
            <ul>
                <li><strong>3:  No transformation debug - </strong>Additional information will be displayed at
                each step of the ADI process, but transformation parameter details will not be included.</li>
                <li><strong>4:  Include transformation debug - </strong>Same as level 3, with the addition of
                    a detailed dump of transformation parameters used for the process.</li>
            </ul>

            <div className={dataLocationDiv}>
                Note that you will also have the option of dumping intermediate
                datasets to file after each ADI phase.  See the info box on the Include Dataset Dumps field for
                more details.
            </div>
        </SimpleMessage>
    );
    static datasetDumps = (
        <SimpleMessage>
            <div className={dataLocationDiv}>
                If set to YES, then the intermediate dataset created at each phase of the ADI process will be
                dumped to file for fine-grained debugging.  Specifically, datasets will be dumped at the following
                times during a processing interval:
                <ul>
                    <li>Post-retrieval</li>
                    <li>Pre-transform</li>
                    <li>Post-transform</li>
                    <li>Pre-process data</li>
                    <li>Post-process data</li>
                </ul>
            </div>

        </SimpleMessage>
    );

    static contactEmail = 'Who should be contacted in case of questions or problems?';
    static runContactEmail = 'Who should be contacted when the process completes?';
    static startDate = 'Enter the begin date in the form YYYYMMDD.[hh[mm[ss]]].';

    static endDate = (
        <SimpleMessage>
            Enter the end date in the form YYYYMMDD.[hh[mm[ss]]].  Data will be processed up to but not including this date.
        </SimpleMessage>
    );
    static dimensionInterval = 'bin width';
    static dimensionStart = 'First dimension value';
    static dimensionEnd = 'Last dimension value';
    static dimensionLength = '# of bins';
    static inputDataset = 'An input dataset is a group of datastreams in a hierarchal order ' +
        'reflecting most to least preferred.';
    static inputDatasetName = '';

    static inputDatasetOffsets = (
        <SimpleMessage>
            The amount of extra data to retrieve in addition to data corresponding to the length
            of the processing_interval defined on the Process Overview form
        </SimpleMessage>
    );

    static inputDatasetOffsetStart = 'Amount of extra data to retrieve before the beginning of processing interval';
    static inputDatasetOffsetEnd = 'Amount of extra data to retrieve from end of processing interval';
    static retrievalRules = 'Determine preferred and alternate datastreams from which to retrieve data and the rules that define their preference.';
    static retrievalRulesPriority = '';
    static retrievalRulesRunLocation = '';
    static retrievalRulesStart = '';
    static retrievalRulesEnd = '';
    static retrievalRulesDataLocation = '';
    static retrievalRulesDatastreamVariable = '';
    static retrievalRulesRetrievedVariable = '';
    static inputMappingsDatastreamVariable = '';
    static inputMappingsRetrievedVariable = '';

    static retrievedVariableRequired = (
        <SimpleMessage>
            If yes, the process will fail if it can't find input data for the variable over the time period being processed.
        </SimpleMessage>
    );

    static retrievedVariableQCVariable = (
        <SimpleMessage>
            If yes, the process will automatically retrieve not only the variable, but also its companion qc variable.
        </SimpleMessage>
    );

    static retrievedVariableQCRequired = (
        <SimpleMessage>
            If yes and the process successfully retrieves data for the variable, but it cannot find companion qc data for the same time period,
            the process will fail.
        </SimpleMessage>
    );

    static retrievedVariable = (
        <SimpleMessage>
            Retrieved variables are uniquely named representations of the data
            retrieved from the most preferred available datastream of this
            variable’s input dataset as defined in the first column. By default
            the name assigned is the name as given in the highest priority
            datastream in its input dataset.
        </SimpleMessage>
    );

    static coordinateSystem = (
        <SimpleMessage>
            <p>Coordinate Systems define the coordinate grids available to the process.</p>
            <p>When a coordinate system is assigned to a retrieved variable, the process will
                automatically transform that variable’s data onto the respective grid.</p>
        </SimpleMessage>
    );

    static outputDatastream = (
        <SimpleMessage>
            <p>Output Datastreams are the datastreams that are available to this
                process to store data.</p>
            <p>When a retrieved variable is assigned to an output datastream,
                that variable’s data will be automatically passed into the output
                datastream with the specified datastream variable name.
            </p>
            <p>If a retrieved variable is assigned to an output datastream and that
                variable also has been assigned to a coordinate system, then that output
                datastream will have the same shape as the coordinate system.
            </p>

        </SimpleMessage>
    );

    static outputMapping = (
        <SimpleMessage>
        </SimpleMessage>
    );

    /**
     * Help used in New Process Wizard
     */
    static advancedSettings = (
        <SimpleMessage>
            Enable advanced settings if you need to customize data transformations and output QC reporting.
        </SimpleMessage>
    );

    static outputQC = (
        <SimpleMessage>
            The bin average transform method provides metrics that can be applied based on thresholds.
            The metrics applied include the standard deviation of the points used in the average and a fractional
            indicator of the number of good points available in the averaging window.
        </SimpleMessage>
    );

    static inputVariables = (
        <SimpleMessage>
            <p>Select the datastream variables that you would like to use as input to this process.</p>
            <p>(Variables are grouped by datastream.)</p>
        </SimpleMessage>
    );

    static TRANSFORMATION_TYPE_DESCRIPTIONS = [
        {
            key: '1',
            ttype: 'Auto',
            description: (
                <div>
                    <p>The transformation type will be determined automatically based upon the ratio of the output bin
                        size compared to the input bin size:</p>
                    <ul>
                        <li>If the output bin is LARGER than the input bin, Bin Averaging will be used.</li>
                        <li>If the output bin is SMALLER than the input bin, then Bilinear Interpolation will be used.</li>
                        <li>If the output bin is EQUAL to the input bin, then Passthrough will be used.</li>
                    </ul>
                </div>
            )
        },
        {
            key: '2',
            ttype: 'Bin Averaging',
            description: 'Good input values falling within the specified output bin will be averaged together to form the output value.'
        },
        {
            key: '3',
            ttype: 'Bilinear Interpolation',
            description: 'Output values will be interpolated from the two nearest good bounding values in the input.'
        },
        {
            key: '4',
            ttype: 'Nearest Neighbor (i.e., subsample)',
            description: 'The closest good input value to the output point will be used as the output value.'
        },
        {
            key: '5',
            ttype: 'Passthrough',
            description: (<p>No transformation will be applied.  <strong>NOTE: If the input bin is not equal to the output bin, this will cause an error.</strong></p>)
        },
    ];
    static TRANSFORMATION_TYPE_TABLE_COLUMNS = [
        {
            title: 'Transformation Type',
            dataIndex: 'ttype',
            key: 'ttype',
            render: ttype => {
                return <strong>{ttype}</strong>;
            }
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description'
        }
    ];

    static transformationType = (
        <SimpleMessage wrapperStyle={templateTableStyle}>
            <p>ADI will perform a transformation to map the retrieved data onto the coordinate grid specified for the
                output datastream.  The transformation type parameter specifies the algorithm that will be used to
                perform this transformation.  The following options are available:
            </p>
            <Table dataSource={InfoMessages.TRANSFORMATION_TYPE_DESCRIPTIONS}
                columns={InfoMessages.TRANSFORMATION_TYPE_TABLE_COLUMNS} pagination={false}/>
        </SimpleMessage>
    );

    static binWidth = (
        <SimpleMessage>
            <p>The width parameter gives the width of each bin in the units of the dimension.
                If not explicitly set, the width is equivalent to the interval. If there are no gaps, and the grid is regularly spaced, this will be equal to the interval parameter.
                If there are gaps in the data, where the gaps exist it will be assumed that there are irregularly shaped input bins,
                which can lead to unexpected output values. To avoid this possibility, explicitly define the width parameter
                of regularly shaped output grids. For typical situations set width equal to interval.</p>
            <p><i>To smooth output data using a rolling mean, set a bin width larger than the interval.</i></p>
        </SimpleMessage>
    );

    static range = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <p>Range is applicable only to interpolation and nearest neighbor transformation methods. When the closest sample in the input data to the target output time is bad or missing the range sets the maximum distance from the target output time that data will be interpolated from the input data.</p>
            <p>Example using nearest neighbor to subsample data</p>
            <p>(uses modified version of met.b1 file)</p>
            <ul>
                <li>Input Data
                    <ul>
                        <li>1 minute intervals</li>
                        <li>end bin alignment</li>
                        <li>missing values from 3:15 through 3:41</li>
                        <li>bad data from 3:42 through 4:04</li>
                        <li>bad data from 4:56 through 5:14</li>
                    </ul>
                </li>
            </ul>
            <img
                src={rangeInputImg}
                alt={'Diagram of input data'}
                className={css`margin-bottom: 15px;`}
            />
            <ul>
                <li>Output Data
                    <ul>
                        <li>nearest neighbor transformation method</li>
                        <li>30 minute intervals</li>
                        <li>range = 5 minutes</li>
                    </ul>
                </li>
            </ul>
            <img
                src={rangeOutputImg}
                alt={'Diagram of output data'}
                className={css`margin-bottom: 15px;`}
            />
            <ul>
                <li>Sample at 3:30 is missing because the closest good samples to 3:30 is 3:14 which is &gt; 5 minutes from 3:30</li>
                <li>Sample at 4:00 is indeterminate because the closest good sample to 4:00 is 4:05 which is &lt; 5 minutes from 4:00 because
                    <ul>
                        <li>alignment of input is end and prior to transformation all inputs are moved to center alignment. The move to center alignment from end shifts the data back &frac12; bin (30 seconds) in time that moves the &lsquo;good&rsquo; data at 4:05 back to 4:04:30 which is &lt; 5 minutes from 4.</li>
                    </ul>
                </li>
                <li>Sample at 5:00 is missing because closest good sample is 4:55, but
                    <ul>
                        <li>alignment of input is end and prior to transformation all inputs are moved to center alignment. The move to center alignment from end shifts the data back &frac12; bin (30 seconds) in time that moves the &lsquo;good&rsquo; data at 4:55 back to 4:54:30 which is &gt; 5 minutes from 5:00.</li>
                    </ul>
                </li>
            </ul>
            <p>Relationship of alignments illustrating moving from end to center is a shift back in time is noted below:</p>
            <img
                src={rangeAlignmentImg}
                alt={'Diagram of relationship of alignments'}
                className={css`margin-bottom: 10px;`}
            />
        </SimpleMessage>
    );

    static qcMask = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <p>The qc_mask transform parameter is used to change the interpretation of bit packed qc quality assessments found in the input data.&nbsp; All bits defined in qc_mask will be considered and all other non-zero qc bits will be considered indeterminate.</p>
            <p><strong>Example application of qc_ mask transform parameter</strong></p>
            <p>(uses modified version of met.b1 file)</p>
            <ul>
                <li>Input Data
                    <ul>
                        <li>1 minute intervals</li>
                        <li>end alignment</li>
                        <li>modified to est bit 4 for the first portion of the day and bit 3 for the last portion. Both bits resolve to an indeterminate condition.&nbsp;</li>
                        <li>bit 3 will be masked to be bad using qc_mask.</li>
                    </ul>
                </li>
            </ul>
            <img
                src={qcMaskInputImg}
                alt={"diagram of QC mask input"}
                className={css`margin-bottom: 10px;`}
            />
            <ul>
                <li>Output data
                    <ul>
                        <li>Transformation applied
                            <ul>
                                <li>The one minute data is averaged to a 5 minute grid</li>
                                <li>qc_mask has been to interpret bits 1, 2, and 3 as bad</li>
                            </ul>
                        </li>
                    </ul>
                </li>
            </ul>

            <img
                src={qcMaskOutputImg}
                alt={"diagram of QC mask output"}
                className={css`margin-bottom: 10px;`}
            />
        </SimpleMessage>
    );

    static qcBad = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <ul>
                <li>
                    <p>The qc_bad transform parameter is used to change the interpretation of integer-based qc quality assessments found in the input data. &nbsp;<br /> <br /> </p>
                    <p>ARM data standards support three quality assessments, &lsquo;good&rsquo;, &lsquo;bad&rsquo;, and &lsquo;indeterminate.&nbsp;</p>
                    <ul>
                        <li>Good assessments are indicated with an integer value of zero.</li>
                        <li>Indeterminate assessment indicates data has some characteristic or quality that indicates a problem could exist, but it cannot be confirmed.</li>
                        <li>A bad assessment flags data that should not be used. <br /> </li>
                    </ul>
                    <p>QC bad can be used to evaluate indeterminate assessments as bad or bad assessments as indeterminate.</p>
                    <ul>
                        <li>All integers defined in qc_bad will be interpreted as a &lsquo;bad&rsquo; state.&nbsp;</li>
                        <li>All other non-zero integer values will be considered indeterminate.</li>
                    </ul>
                    <p><strong>Example application of qc_bad transform parameter:<br /> </strong>(input data from a sample process)</p>
                    <ul>
                        <li>Input Data
                            <ul>
                                <li>a modified version of hourly armbecldrad.c1 data modified to view in daily files</li>
                                <li>Period from late march 2013 has:
                                    <ul>
                                        <li>an indeterminate sample around noon 20130320</li>
                                        <li>a bad sample around noon 20130326 that has been set to missing_value</li>
                                    </ul>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </li>
            </ul>
            <img
                src={qcBadInputImg}
                alt={"diagram of QC bad transformation"}
                className={css`margin-bottom: 10px;`}
            />
            <ul>
                <li>Output Data
                    <ul>
                        <li>qc_bad has been set to 1, 2, 3, 4 so that all integer qc flags are considered bad.</li>
                    </ul>
                </li>
            </ul>

            <img
                src={qcBadOutputImg}
                alt={"diagram of QC bad transformation"}
                className={css`margin-bottom: 10px;`}
            />
            <p>The indeterminate bit is noted in red to denote bad assessment.</p>
        </SimpleMessage>
    );

    static noQCBad = (
        <SimpleMessage>
            <p>If applied to QC variables that do not conform to ARM standards, there is no mechanism to interpret any QC values as indeterminate. If the original QC is bit packed, set qc_bad for a particular bit by setting all possible variation of values that can be represented.</p>
            <p>For example, if input QC has 4 bits and bit 2 is bad, then qc_bad must = 2, 3, 6, 7, 10, 11, 14, 15.</p>
        </SimpleMessage>
    );

    static alignment = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <p>Alignment documents the point within an averaging bin the coordinate value in the data is representing.</p>
            <p>How changing alignment alters the representation of the data is illustrated by interpolating a datastream representing averaged data with an end alignment onto itself and moving the alignment to the beginning of bin of time. &nbsp;</p>
            <p><strong>Example application of alignment parameter</strong></p>
            <p>(uses actual sirs.b1 file)</p>
            <ul>
                <li>Input Data
                    <ul>
                        <li>Input is sirs.b1</li>
                        <li>60 second sampling period</li>
                        <li>end of bin alignment</li>
                    </ul>
                </li>
            </ul>
            <ul>
                <li>Output Data
                    <ul>
                        <li>transformation method = interpolation</li>
                        <li>60 second sampling interval</li>
                        <li>Start of alignment</li>
                        <li>Range = 0<br /> This results in no estimation of values, if input doesn&rsquo;t have a value then output will not have a value.</li>
                    </ul>
                </li>
            </ul>
            <img
                src={alignmentOutputImg}
                alt={"diagram of output data"}
                className={css`margin-bottom: 10px;`}
            />
            <p>
                An examination of the input and output data more clearly shows a move from end to begin alignment results in the value input data at time t+60 seconds being documented in the output file at t = 60 seconds in output.  The value at time 0 in the input file will shift to the last sample in the preceeding day’s file.
            </p>
            <p>Input:</p>
            <img
                src={alignmentInputDataImg}
                alt={"diagram of input data"}
                className={css`margin-bottom: 10px;`}
            />
            <p>Output:</p>
            <img
                src={alignmentOutputDataImg}
                alt={"diagram of output data"}
                className={css`margin-bottom: 10px;`}
            />
            <p>The shift of 60 seconds backward in time allows the representation of the period (i.e. bin) from 0 seconds to 60 seconds to remain correct.  With end alignment the value at time 60 represents the bin from 0 to 60.  For begin alignment the value at 0 represents the bin of data from 0 to 60.  The need for the shift in the plotted data is illustrated below.</p>
            <img
                src={alignmentShiftImg}
                alt={"diagram showing shift in alignment"}
                className={css`margin-bottom: 10px;`}
            />
        </SimpleMessage>
    );

    static standardDeviation = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <p>Standard Deviation is applicable only to bin averaged transformation method.&nbsp; It is a fractional indicator of the number of &lsquo;good&rsquo; points (Note indeterminate points are considered good) from the input datastream used to calculate the averaged value in the transformed data.</p>
            <p>(Process = pcm_transform_parms_std</p>
            <p><strong>Example application of standard deviation transform parameter</strong></p>
            <p>(uses actual met.b1 file)</p>
            <ul>
                <li>Input is met.b1
                    <ul>
                        <li>Sampling interval = 1 minute</li>
                        <li>Alignment = end</li>
                        <li>Day is 20190401</li>
                    </ul>
                </li>
            </ul>
            <img
                src={standardDeviationInputImg}
                alt={"diagram of standard deviation input"}
                className={css`margin-bottom: 10px;`}
            />
            <p>
                An examination of the input and output data more clearly shows a move from end to begin alignment results in the value input data at time t+60 seconds being documented in the output file at t = 60 seconds in output.  The value at time 0 in the input file will shift to the last sample in the preceeding day’s file.
            </p>
            <ul>
                <li>Output before good fraction limit applied
                    <ul>
                        <li>Transform = bin average with 10 minute sampling interval</li>
                        <li>Center bin alignment</li>
                        <li>Standard deviation bad limit = 50%</li>
                        <li>Standard deviation indeterminate limit = 35%</li>
                    </ul>
                </li>
            </ul>
            <img
                src={standardDeviationOutputImg}
                alt={"diagram of standard deviation output"}
                className={css`margin-bottom: 10px;`}
            />
        </SimpleMessage>
    );

    static goodFrac = (
        <SimpleMessage wrapperStyle={transformParameterMessageStyles}>
            <p>Percent Good Fraction is applicable only to bin averaged transformation method. It is a fractional indicator of the number of 'good' points (Note indeterminate points are considered good) from the input datastream used to calculate the averaged value in the transformed data.</p>
            <p>(Process = pcm_transform_parms_goodfrac (a reuse of pcm_transform_parms_qcmask with good fraction limit applied)</p>
            <p><strong>Example application of good fraction transform parameter</strong></p>
            <p>(uses modified version of met.b1 file)</p>
            <ul>
                <li>Input Data<br /> (uses a modified version of met.b1 data file)
                    <ul>
                        <li>Sampling interval = 1 minute</li>
                        <li>Alignment = end</li>
                        <li>Day is 20190401</li>
                        <li>40% of samples are bad in first hour of day.</li>
                        <li>80% of samples are bad in 3<sup>rd</sup> hour of day.</li>
                        <li>80% of samples are indeterminate in 4<sup>th</sup> hour of day.</li>
                    </ul>
                </li>
            </ul>
            <img
                src={goodfracInputImg}
                alt={"diagram of good fraction on input"}
                className={css`margin-bottom: 10px;`}
            />
            <ul>
                <li>Output Data
                    <ul>
                        <li>Transform = bin average with 10 minute sampling interval</li>
                        <li>Center bin alignment</li>
                        <li>Good fraction indeterminate limit = 80%</li>
                        <li>Good fraction bad limit = 50%</li>
                    </ul>
                </li>
            </ul>
            <img
                src={goodfracOutputImg}
                alt={"diagram of good fraction on output"}
                className={css`margin-bottom: 10px;`}
            />
            <ul>
                <li>With 40% of samples good in first hour set to bad that leaves 60% good, thus the good fraction indeterminate limit of 80% fails, but the bad limit of 50% does not.</li>
                <li>With 80% of samples in 3<sup>rd</sup> hour set to bad that leaves 20% good, thus failing the 50% good fraction bad limit.</li>
                <li>The indeterminate values at hour 4 are considered good with respect to good fraction limits</li>
                <li>The first sample of day fails good fraction bad limit set because goodfraction of first sample is 0.333.</li>
            </ul>
            <p>This occurs because</p>
            <ul>
                <li>First 4 samples in first hour have been set to missing with bad qc</li>
            </ul>
            <ul>
                <li>The 1 minute input is end alignment, thus the first bad sample in the 20140401 file covers 23:59:00 to 23:59:59 of 20140331.</li>
                <li>The output interval is 10 minutes with center alignment. That means its value will be the average of the last 5 minutes of data from 20140331 and the first 5 minutes of data from 20140401.
                    <ul>
                        <li>Because input datastream is end alignment we know 1 sample from 20140331 is bad and the first 3 samples in 20140401 are bad.</li>
                        <li>The remaining 2 samples in the first 5 minutes of 20140401 are good.</li>
                        <li>We do not know anything about the 4 samples from 23:55:00 to 23:58:59 so that will not contribute to the total.</li>
                        <li>Thus %good fraction = 2/6 or 1/3.</li>
                    </ul>
                </li>
            </ul>
            <p>First 15 samples from input file</p>
            <p>temp_mean = -9999, -9999, -9999, -9999, -9.8, -9.8, -9.81, -9.84, -9.96, -9.98, -9999, -9999, -9999, -9999, -9.99, -10.06</p>
            <p>qc_met_temp_mean = 2, 2, 2, 2, 0, 0, 0, 0, 0, 0, 2, 2, 2, 2, 0, 0</p>
            <p>good fraction of first 2 samples in output file.</p>
            <p>met_temp_mean_goodfraction = 0.3333333, 0.6,</p>
            <img
                src={goodfracDiagramImg}
                alt={"diagram of good fraction"}
                className={css`margin-bottom: 10px;`}
            />
        </SimpleMessage>
    );

    static projectName = (
        <SimpleMessage>
            <p>This is the name of the code project folder that will be created.</p>
        </SimpleMessage>
    );

    static authorName = (
        <SimpleMessage>
            <p>Who is the author of this code project?</p>
        </SimpleMessage>
    );

    static phoneNumber = (
        <SimpleMessage>
            <p>What is the author's phone number in order to contact them?</p>
        </SimpleMessage>
    );

    static emailAddress = (
        <SimpleMessage>
            <p>What is the author's email address in order to contact them?</p>
        </SimpleMessage>
    );

    static vapTemplateOptions = (
        <SimpleMessage wrapperStyle={templateTableStyle}>
            <Table dataSource={Constants.VAP_TEMPLATE_LIST} columns={Constants.TEMPLATE_TABLE_COLUMNS} pagination={false}/>
        </SimpleMessage>
    );

    static ingestTemplateOptions = (
        <SimpleMessage wrapperStyle={templateTableStyle}>
            <Table dataSource={Constants.INGEST_TEMPLATE_LIST} columns={Constants.TEMPLATE_TABLE_COLUMNS} pagination={false}/>
        </SimpleMessage>
    );

    static availableUsersHelp = (
        <SimpleMessage>
            <p>The available user list is anyone who has logged in or edited a DOD.</p>
            <p>If you are looking for a user that is not currently showing up in this list, have them first log in with their ARM credentials and this will add them to the available users.</p>
        </SimpleMessage>
    );

    render() {
        return null;
    }
}



// WEBPACK FOOTER //
// ./src/components/core/help/InfoMessages.js